import React, { FC, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { Row, Col } from "antd";
import { useHandleForm } from "../../../hooks/useHandleForm";
import {
  AutoComplete,
  DraggableModal,
  ExpandedTable,
  FormBlock,
  FormField,
  FormInterval,
  IButton,
  IDatePicker,
  ISelect,
} from "../../../ui-kit";
import usersStore from "../../../store/users";
import { useStatusMessage } from "../../../hooks/useStatusMessage";

import { Controller } from "react-hook-form";
import api from "../api";

import locale from "antd/es/date-picker/locale/ru_RU";

import styles from "../Users.module.scss";
import { convertDate } from "../../../utils/helpers/data.helpers";
import HistoryModal from "../../../ui-kit/HistoryModal/HistoryModal";
import schema from "./validateSchema";

const UserForm: FC = observer((values) => {
  const [organization, setOrganization] = useState(null);
  const { roles } = usersStore;
  const [lockVisible, setLockVisible] = useState(false);
  const [historyVisible, setHistoryVisible] = useState(false);

  const initialValues = {
    id: values?.record?.id,
    userName: values?.record?.userName,
    organizationId: values?.record?.organizationId,
    organizationName: values?.record?.organizationName,
    name: values?.record?.name,
    surname: values?.record?.surname,
    patronymic: values?.record?.patronymic,
    position: values?.record?.position,
    userGroupId: values?.record?.userGroupId,
    email: values?.record?.email,
    phoneNumber: values?.record?.phoneNumber,
    isDisabled: values?.record?.isDisabled,
    lockoutEnd: values?.record?.lockoutEnd,
    snils: values?.record?.snils,
  };
  const initialFocus = {};
  const {
    errors,
    register,
    handleSubmit,
    watchAllField,
    isFocused,
    handleBlur,
    handleFocus,
    control,
    setValue,
  } = useHandleForm(schema, initialValues, initialFocus);

  const onSubmit = (values) => {
    usersStore.saveUser(values);
  };

  useEffect(() => {
    if (organization) {
      setValue("organizationId", organization?.id);
    }
    return;
  }, [organization]);

  const onLockUser = async () => {
    await usersStore.lockUser(
      values.record.userId,
      watchAllField.dtLockout?.format("YYYY-MM-DD"),
    );
    setValue("dtLockout", initialValues.lockoutEnd);
    setLockVisible(false);
  };

  const onUnlockUser = () => {
    usersStore.unlockUser(values.record.userId);
  };

  const columns = [
    {
      title: "Дата изменения",
      dataIndex: "dt",
      key: "dt",
      render: (dt) => (dt ? convertDate(dt) : ""),
    },
    {
      title: "Поле",
      dataIndex: "attr",
      key: "attr",
    },
    {
      title: "Старое значение",
      dataIndex: "oldValue",
      key: "oldValue",
    },
    {
      title: "Новое значение",
      dataIndex: "newValue",
      key: "newValue",
    },
  ];

  const btnHistory = useRef(null);

  return (
    <>
      <form
        className={styles.SmsSettingsParamsForm}
        onSubmit={handleSubmit(onSubmit)}
      >
        <FormBlock>
          <Row gutter={[15, 20]}>
            <Col span={8}>
              <FormField
                label="Имя пользователя"
                name="userName"
                error={errors.userName?.message}
                register={register}
                type="text"
                isFocused={isFocused.userName}
                currentValue={watchAllField.userName}
                onFocus={handleFocus}
                onBlur={handleBlur}
                isRequired
              />
            </Col>
            <Col span={8}>
              <Controller
                control={control}
                name="userGroupId"
                render={({ field }) => (
                  <ISelect
                    error={errors.userGroupId?.message}
                    field={field}
                    optionsArr={roles?.filter((item)=> !item.isDeleted)}
                    placeholder="Роль"
                    isRequired
                  />
                )}
              />
            </Col>
            <Col span={8} className="form-block__data-header">
              <FormField
                error={errors.email?.message}
                label="Email"
                name="email"
                register={register}
                type="email"
                isRequired
                isFocused={isFocused.email}
                currentValue={watchAllField.email}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </Col>
            <Col span={8} className="form-block__data-header">
              <FormField
                error={errors.surname?.message}
                label="Фамилия"
                name="surname"
                register={register}
                type="text"
                isFocused={isFocused.surname}
                currentValue={watchAllField.surname}
                onFocus={handleFocus}
                onBlur={handleBlur}
                isRequired
              />
            </Col>
            <Col span={8} className="form-block__data-header">
              <FormField
                error={errors.name?.message}
                label="Имя"
                name="name"
                register={register}
                type="text"
                isFocused={isFocused.name}
                currentValue={watchAllField.name}
                onFocus={handleFocus}
                onBlur={handleBlur}
                isRequired
              />
            </Col>
            <Col span={8} className="form-block__data-header">
              <FormField
                label="Отчество"
                name="patronymic"
                register={register}
                type="text"
                isFocused={isFocused.patronymic}
                currentValue={watchAllField.patronymic}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </Col>
            <Col span={8} className="form-block__data-header">
              <FormField
                label="СНИЛС"
                name="snils"
                type="text"
                register={register}
                isFocused={isFocused.snils}
                currentValue={watchAllField.snils}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </Col>
            <Col span={8} className="form-block__data-header">
              <FormField
                label="Должность"
                name="position"
                register={register}
                type="text"
                isFocused={isFocused.position}
                currentValue={watchAllField.position}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </Col>
            <Col span={8} className="form-block__data-header">
              <FormField
                label="Телефон"
                name="phoneNumber"
                register={register}
                type="tel"
                isFocused={isFocused.phoneNumber}
                currentValue={watchAllField.phoneNumber}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </Col>
            <Col span={24}>
              <Controller
                control={control}
                name="organizationName"
                render={({ field }) => (
                  <AutoComplete
                    field={field}
                    label="Организация"
                    required
                    getData={api.getAllOrganizations}
                    onSelect={setOrganization}
                    error={errors.organizationName?.message}
                  />
                )}
              />
            </Col>
            <Col span={2} className="form-block__data-header">
              <IButton className={styles.UsersButton} type="submit">
                Сохранить
              </IButton>
            </Col>
            {(values.record?.userId && values?.record?.isDisabled) ||
            values?.record?.lockoutEnd ? (
              <Col span={4} className="form-block__data-header">
                {" "}
                <IButton
                  onClick={onUnlockUser}
                  className={styles.UsersButton}
                  type="button"
                >
                  Разблокировать
                </IButton>
              </Col>
            ) : null}
            {values.record?.userId && !values?.record?.isDisabled ? (
              <>
                <Col span={3} className="form-block__data-header">
                  <IButton
                    onClick={() => {
                      setLockVisible(true);
                    }}
                    className={styles.UsersButton}
                    type="button"
                  >
                    Заблокировать
                  </IButton>
                </Col>
              </>
            ) : null}
            {/* <Col span={12} className="form-block__data-header">
              <div ref={btnHistory} id="btn-history">
                <IButton
                  className={styles.UsersButton}
                  onClick={() => setHistoryVisible(true)}
                  type="button"
                >
                  История изменений
                </IButton>
              </div>
            </Col> */}
          </Row>
        </FormBlock>
      </form>
      {/* {historyVisible && (
        <HistoryModal
          open={historyVisible}
          onCancel={() => setHistoryVisible(false)}
          title={"История изменений"}
          historyUrl={`Admin/User/GetHistory/${values.record.id}`}
          width="fit-content"
        />
      )} */}
      {lockVisible && (
        <DraggableModal
          visible={lockVisible}
          title={"Блокировка пользователя"}
          onCancel={() => {
            setLockVisible(false);
          }}
          footer={
            <>
              <IButton
                className={styles.UsersButton}
                onClick={onLockUser}
                type="button"
              >
                Заблокировать
              </IButton>
              <IButton
                className={styles.UsersButton}
                onClick={() => {
                  setLockVisible(false);
                }}
                type="button"
              >
                Отмена
              </IButton>
            </>
          }
        >
          <Controller
            control={control}
            name="dtLockout"
            render={({ field }) => (
              <IDatePicker
                locale={locale}
                field={field}
                placeholder={"Дата окончания блокировки"}
              />
            )}
          />
        </DraggableModal>
      )}
    </>
  );
});

export default UserForm;
